import { BlockProps } from '@/components/Block/Block';
import CHARACTERS from '@/resources/Characters';
import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import BlockEditorContainer from '../../BlockEditorContainer';
import UploadButton from '@/components/Redactie/UploadButton/UploadButton';
import { IconButtonVariant } from '@/components/Redactie/IconButton/IconButton';
import BlockEditorCharacterPicker from '../../BlockEditorCharacterPicker';
import { ChatBlockProfilePicture } from '../ChatTextBlock/ChatTextBlock';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import P from '@/components/Redactie/P/P';
import styles from './ChatAudioBlock.module.sass';
import { DotPulse } from '@uiball/loaders';
import Icon from '@mdi/react';
import {
    mdiArrowRightCircle,
    mdiChevronDown,
    mdiPause,
    mdiPlay,
} from '@mdi/js';
import { getAudioData, linearPath } from '@/utils/waveFormPath';
import TextButton from '@/components/Button/TextButton';
import useLocalStorage from '@/hooks/useLocalStorage';
import useAnimationFrame from '@/hooks/useAnimationFrame';

interface ChatAudioBlockData {
    character: string;
    transcript: string;
}

const ChatAudioBlock = ({
    uuid,
    data,
    media,
    attachments,
    isEditable,
    isAnimated,
    activeChatCharacter,
    onContentHeightChanged,
    onFinished,
    onEdited,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<ChatAudioBlockData>({
        character:
            data.character ??
            activeChatCharacter?.name ??
            Object.keys(CHARACTERS)[0],
        transcript:
            data.transcript?.trim() ??
            'Voeg een audiodescriptie toe volgens de toegankelijkheidsrichtlijnen.',
    });

    if (isEditable) {
        return (
            <ChatAudioBlockEditor
                uuid={uuid}
                blockData={blockData}
                media={media}
                attachments={attachments}
                setBlockData={setBlockData}
                onEdited={onEdited}
            />
        );
    } else {
        return (
            <ChatAudioBlockViewer
                uuid={uuid}
                blockData={blockData}
                media={media}
                isAnimated={isAnimated}
                onContentHeightChanged={onContentHeightChanged}
                onFinished={onFinished}
            />
        );
    }
};

const ChatAudioBlockEditor = ({
    uuid,
    media,
    attachments,
    blockData,
    setBlockData,
    onEdited,
}: {
    uuid: BlockProps['uuid'];
    media: BlockProps['media'];
    attachments: BlockProps['attachments'];
    blockData: ChatAudioBlockData;
    setBlockData: (data: ChatAudioBlockData) => void;
    onEdited: BlockProps['onEdited'];
}) => {
    const [audioFile, setAudioFile] = useState<File | null>(attachments?.image);
    const audioFileBase64Url = useMemo(() => {
        if (!audioFile) return null;
        return URL.createObjectURL(audioFile);
    }, [audioFile, media?.audio?.url]);
    const hasAudioFile = !!audioFile || !!media?.audio?.url;

    useEffect(() => {
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: audioFile
                ? {
                      audio: audioFile,
                  }
                : {},
        });
    }, [blockData, audioFile]);

    return (
        <BlockEditorContainer
            className={`bg-${
                CHARACTERS[blockData.character].color
            } bg-opacity-50`}
        >
            <div className="flex gap-2 items-center">
                <BlockEditorCharacterPicker
                    characterName={blockData.character}
                    setCharacterName={(characterName) => {
                        setBlockData({
                            ...blockData,
                            character: characterName,
                        });
                    }}
                />
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio
                    className="w-full grow"
                    src={audioFileBase64Url ?? media?.audio?.url}
                    controls
                />
                <UploadButton
                    label="Audio kiezen"
                    className="shrink-0 self-center"
                    variant={
                        !hasAudioFile
                            ? IconButtonVariant.Primary
                            : IconButtonVariant.Regular
                    }
                    accept="audio/mp3"
                    onChange={(files) => {
                        if (files && files.length > 0) {
                            setAudioFile(files[0]);
                        }
                    }}
                >
                    Upload
                </UploadButton>
            </div>
            <P
                isEditable
                rows={5}
                value={blockData.transcript}
                onChange={(value) => {
                    setBlockData({
                        ...blockData,
                        transcript: value,
                    });
                }}
                placeholder="Audiodescriptie"
            />
        </BlockEditorContainer>
    );
};

// Format audio time to minutes and seconds
const formatAudioTimeToUI = (seconds: number, isShort = true) => {
    const minutes = Math.floor(seconds / 60);
    const secondsLeft = Math.floor(seconds % 60);

    if (isNaN(secondsLeft)) return '';
    else if (isShort)
        return `${minutes}:${secondsLeft < 10 ? '0' : ''}${secondsLeft}`;
    else return `${minutes} minuten en ${secondsLeft} seconden`;
};

const ChatAudioBlockViewer = ({
    uuid,
    blockData,
    media,
    isAnimated,
    onContentHeightChanged,
    onFinished,
}: {
    uuid: BlockProps['uuid'];
    blockData: ChatAudioBlockData;
    media: BlockProps['media'];
    isAnimated: BlockProps['isAnimated'];
    onContentHeightChanged: BlockProps['onContentHeightChanged'];
    onFinished: BlockProps['onFinished'];
}) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const audioId = useId();
    const detailsId = useId();
    const shouldReduceMotion = useReducedMotion();

    const [hasFinished, setHasFinished] = useState<boolean>(!isAnimated);
    const [isSending, setIsSending] = useState<boolean>(!!isAnimated);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentAudioTimeInSeconds, setCurrentAudioTimeInSeconds] =
        useState(0);
    const [waveFormPath, setWaveFormPath] = useState<string | null>(null);

    useEffect(() => {
        setTimeout(() => {
            if (isSending) {
                setIsSending(false);
                onContentHeightChanged?.();
            }
        }, 1250);

        (async () => {
            if (!media?.audio?.url) return;
            const audioData = await getAudioData(media?.audio?.url);
            if (audioData)
                setWaveFormPath(
                    linearPath(audioData, {
                        width: 64,
                        height: 12,
                        samples: 32,
                        type: 'mirror',
                        paths: [{ d: 'V', sy: 0, x: 50, ey: 100 }],
                    }),
                );
            onContentHeightChanged?.();
        })();
    }, [isSending, setIsSending, onContentHeightChanged]);

    // Call onFinished when audio has finished playing
    const onEnded = () => {
        if (hasFinished) return;
        onFinished?.();
        setHasFinished(true);
    };
    // Update isPlaying state
    const onPlayUpdate = () => setIsPlaying(!audioRef.current?.paused);
    // Update currentAudioTimeInSeconds state
    const onTimeUpdate = () => {
        const seconds = Math.floor(audioRef.current?.currentTime ?? 0);
        if (seconds !== currentAudioTimeInSeconds)
            setCurrentAudioTimeInSeconds(seconds);

        // If there are less than 2 seconds left, call onEnded
        if (
            audioRef.current?.duration &&
            audioRef.current?.currentTime &&
            audioRef.current?.duration - audioRef.current?.currentTime < 2
        ) {
            onEnded();
        }
    };

    const audioLengthInSeconds = Math.floor(audioRef?.current?.duration ?? 0);

    return (
        <section
            aria-label="Audiobericht"
            aria-details={detailsId}
            className={styles.chataudioblock}
        >
            <ChatBlockProfilePicture
                name={blockData.character}
                isTyping={isSending}
            />
            {isSending ? (
                <motion.button
                    layoutId={
                        isAnimated && !shouldReduceMotion
                            ? `chataudioblock__bubble-${uuid}`
                            : undefined
                    }
                    layoutDependency={isSending}
                    className={`${styles['chataudioblock__bubble']} ${styles['chataudioblock__bubble--sending']}`}
                    whileTap={{
                        scale: !shouldReduceMotion ? 0.9 : 1,
                    }}
                    whileHover={{
                        scale: !shouldReduceMotion ? 1.1 : 1,
                    }}
                    onClick={() => {
                        setIsSending(false);
                    }}
                    title="Open bericht"
                >
                    <span className="sr-only">
                        {`${
                            CHARACTERS[blockData.character].displayName
                        } is aan het typen`}
                    </span>
                    <DotPulse color="currentColor" size={25} />
                </motion.button>
            ) : (
                <motion.div
                    className={styles['chataudioblock__bubble']}
                    layoutId={
                        isAnimated && !shouldReduceMotion
                            ? `chataudioblock__bubble-${uuid}`
                            : undefined
                    }
                >
                    <div
                        className={`bg-${
                            CHARACTERS[blockData.character].color
                        } ${styles['chataudioblock__bubble__content']}`}
                    >
                        <span
                            className={
                                styles['chataudioblock__bubble__content__name']
                            }
                        >
                            {CHARACTERS[blockData.character].displayName}
                            <span className="sr-only"> stuurt:</span>
                        </span>
                        <div
                            aria-label="Audiospeler"
                            role="region"
                            className={
                                styles[
                                    'chataudioblock__bubble__content__player'
                                ]
                            }
                        >
                            <button
                                onClick={() => {
                                    if (audioRef.current?.paused) {
                                        audioRef.current?.play();
                                    } else {
                                        audioRef.current?.pause();
                                    }
                                    setIsPlaying(!audioRef.current?.paused);
                                }}
                                title={isPlaying ? 'Pauzeer' : 'Speel af'}
                                aria-label={isPlaying ? 'Pauzeer' : 'Speel af'}
                                className={
                                    styles[
                                        'chataudioblock__bubble__content__player__playbutton'
                                    ]
                                }
                                aria-controls={audioId}
                            >
                                <Icon
                                    size={1.2}
                                    path={isPlaying ? mdiPause : mdiPlay}
                                />
                            </button>
                            <div>
                                <ChatAudioBlockViewerSlider
                                    audioRef={audioRef}
                                    currentAudioTimeInSeconds={
                                        currentAudioTimeInSeconds
                                    }
                                    audioLengthInSeconds={audioLengthInSeconds}
                                    blockData={blockData}
                                    waveFormPath={waveFormPath}
                                />
                                <div
                                    className={
                                        styles[
                                            'chataudioblock__bubble__content__player__timeline__timestamps'
                                        ]
                                    }
                                >
                                    <span>
                                        <span className="sr-only">
                                            Huidige tijd:{' '}
                                            {formatAudioTimeToUI(
                                                currentAudioTimeInSeconds,
                                                false,
                                            )}
                                        </span>
                                        <span aria-hidden>
                                            {formatAudioTimeToUI(
                                                currentAudioTimeInSeconds,
                                            )}
                                        </span>
                                    </span>
                                    {audioLengthInSeconds > 0 && (
                                        <span>
                                            <span className="sr-only">
                                                Totale lengte:{' '}
                                                {formatAudioTimeToUI(
                                                    audioLengthInSeconds,
                                                    false,
                                                )}
                                            </span>
                                            <span aria-hidden>
                                                {formatAudioTimeToUI(
                                                    audioLengthInSeconds,
                                                )}
                                            </span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                        <audio
                            id={audioId}
                            ref={audioRef}
                            controlsList="nodownload"
                            tabIndex={0}
                            className="absolute opacity-0 scale-0"
                            src={media?.audio?.url}
                            preload="metadata"
                            onEnded={onEnded}
                            onTimeUpdate={onTimeUpdate}
                            onLoadedMetadata={onTimeUpdate}
                            onPlay={onPlayUpdate}
                            onPlaying={onPlayUpdate}
                            onPause={onPlayUpdate}
                        />
                    </div>
                    <ChatAudioBlockViewerTranscript
                        transcript={blockData.transcript}
                        detailsId={detailsId}
                        canFinish={!!(isAnimated && !hasFinished)}
                        onFinished={onFinished}
                    />
                </motion.div>
            )}
        </section>
    );
};

const ChatAudioBlockViewerSlider = ({
    audioRef,
    currentAudioTimeInSeconds,
    audioLengthInSeconds,
    blockData,
    waveFormPath,
}: {
    audioRef: React.RefObject<HTMLAudioElement | null>;
    currentAudioTimeInSeconds: number;
    audioLengthInSeconds: number;
    blockData: ChatAudioBlockData;
    waveFormPath: string | null;
}) => {
    const waveFormOverlayRef = useRef<HTMLDivElement>(null);
    const sliderIndicatorRef = useRef<HTMLDivElement>(null);

    const updatePlayerUI = useCallback(() => {
        // Update the styles on time update outside of react
        if (waveFormOverlayRef.current && sliderIndicatorRef.current) {
            const playedPercentage =
                ((audioRef.current?.currentTime ?? 1) /
                    (audioRef.current?.duration ?? 1)) *
                100;
            waveFormOverlayRef.current.style.width = `min(97.5%, ${
                playedPercentage === 0 ? 97.5 : 100 - playedPercentage
            }%)`;
            sliderIndicatorRef.current.style.left = `min(max(0.25rem, ${playedPercentage}%), calc(100% - 0.25rem))`;
        }
    }, [audioRef, waveFormOverlayRef, sliderIndicatorRef]);

    useAnimationFrame({
        nextAnimationFrameHandler: updatePlayerUI,
    });

    return (
        <div
            className={`group ${styles['chataudioblock__bubble__content__player__timeline']}`}
        >
            {/* WaveForm placeholder until loaded */}
            <div
                className={`absolute inset-x-0 top-1/2 w-full h-1 bg-white rounded-full ${
                    !waveFormPath
                        ? 'scale-y-100 opacity-100'
                        : 'scale-y-0 opacity-0'
                } scale-x-95 -translate-y-1/2 transition-all`}
            />
            {/* WaveForm */}
            <svg
                className={`absolute inset-0 w-full h-full ${
                    waveFormPath
                        ? 'scale-y-100 opacity-100'
                        : 'scale-y-0 opacity-0'
                } origin-center overflow-visible transition-all ease-out duration-300`}
                viewBox="0 0 64 12"
                aria-hidden
            >
                <path
                    d={waveFormPath ?? ''}
                    stroke="currentColor"
                    strokeWidth="0.75"
                    fill="none"
                    strokeLinecap="round"
                />
            </svg>
            {/* Played progress bar overlay to dim unplayed part */}
            <div
                ref={waveFormOverlayRef}
                className={`absolute inset-y-0 right-0 h-full bg-${
                    CHARACTERS[blockData.character].color
                } bg-opacity-50 scale-y-105`}
            />
            {/* Slider input */}
            <input
                type="range"
                value={currentAudioTimeInSeconds}
                min={0}
                max={isNaN(audioLengthInSeconds) ? 0 : audioLengthInSeconds}
                aria-valuetext={`${Math.floor(
                    currentAudioTimeInSeconds,
                )} seconden`}
                onChange={(e) => {
                    if (audioRef.current) {
                        audioRef.current.currentTime = parseInt(e.target.value);
                    }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
            {/* Slider thumb (not interactive) */}
            <div
                ref={sliderIndicatorRef}
                className={`absolute top-1/2 left-0 w-3 h-3 bg-white rounded-full -translate-y-1/2 -translate-x-1/2 overflow-visible`}
            >
                <div
                    className={`w-full h-full bg-${
                        CHARACTERS[blockData.character].color
                    } border-2 border-white opacity-0 group-focus-within:opacity-100 rounded-full shadow-md scale-125 transition-all`}
                />
            </div>
        </div>
    );
};

const ChatAudioBlockViewerTranscript = ({
    transcript,
    detailsId,
    canFinish,
    onFinished,
}: {
    transcript: string;
    detailsId: string;
    canFinish: boolean;
    onFinished?: () => void;
}) => {
    const reduceMotion = useReducedMotion();

    const [isIncreasedTextSizeEnabled] = useLocalStorage(
        'isIncreasedTextSizeEnabled',
        false,
    );

    const [isTranscriptShown, setIsTranscriptShown] = useState(false);

    return (
        <div
            id={detailsId}
            className={`
                ${styles['chataudioblock__bubble__content__player__transcript']}
            `}
        >
            <button
                onClick={() => setIsTranscriptShown(!isTranscriptShown)}
                title={
                    !isTranscriptShown
                        ? 'Geluidsbeschrijving tonen'
                        : 'Geluidsbeschrijving verbergen'
                }
                aria-label="Geluidsbeschrijving tonen"
                aria-expanded={isTranscriptShown}
                className={
                    styles[
                        'chataudioblock__bubble__content__player__transcript__button'
                    ]
                }
            >
                <Icon
                    size={1}
                    path={mdiChevronDown}
                    className={`${
                        styles[
                            'chataudioblock__bubble__content__player__transcript__button__icon'
                        ]
                    } ${!isTranscriptShown ? '' : '-scale-y-100'}`}
                />
                Geluidsbeschrijving
            </button>
            <AnimatePresence>
                {isTranscriptShown && (
                    <motion.div
                        initial={{
                            opacity: 0,
                            height: reduceMotion ? 'auto' : 0,
                        }}
                        animate={{ opacity: [0, 0, 1], height: 'auto' }}
                        exit={{
                            opacity: 0,
                            height: reduceMotion ? 'auto' : 0,
                            marginBottom: 0,
                        }}
                        className={`${
                            styles[
                                'chataudioblock__bubble__content__player__transcript__text'
                            ]
                        } ${isIncreasedTextSizeEnabled ? 'text-lg' : ''}`}
                    >
                        <p
                            className={`${styles['chataudioblock__bubble__content__player__transcript__text__content']} select-text`}
                        >
                            {transcript.split('\n').map((line, index) => (
                                <span key={index} className="select-text">
                                    {line}
                                    <br />
                                </span>
                            ))}
                        </p>
                        {canFinish && !!onFinished && (
                            <div className="overflow-hidden pt-4 pb-8">
                                <TextButton
                                    className="mx-auto"
                                    icon={mdiArrowRightCircle}
                                    onClick={() => {
                                        onFinished();
                                        setIsTranscriptShown(false);
                                    }}
                                >
                                    Volgende
                                </TextButton>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ChatAudioBlock;
